/** Target step count for Demo Guide — shorter paths improve completion rates. */
export const DEMO_WALKTHROUGH_MAX_STEPS = 6;

export interface WalkthroughPlan {
    /** Steps shown in the wizard (focused or full). */
    steps: string[];
    /** Original howTo from the demo catalog. */
    fullSteps: string[];
    /** True when focused mode omits detail steps. */
    truncated: boolean;
}

function sampleEvenly(items: string[], count: number): string[] {
    if (count <= 0) return [];
    if (count >= items.length) return [...items];
    const picked: string[] = [];
    for (let i = 0; i < count; i++) {
        const idx = Math.round((i * (items.length - 1)) / Math.max(count - 1, 1));
        const step = items[idx];
        if (!picked.includes(step)) picked.push(step);
    }
    return picked;
}

/**
 * Build a focused walkthrough: keep setup (first 2), payoff (last), and evenly
 * sample the middle so officers finish ~80% of demos without skipping the catalog.
 */
export function compactWalkthroughSteps(
    howTo: string[] | undefined,
    maxSteps: number = DEMO_WALKTHROUGH_MAX_STEPS,
): WalkthroughPlan {
    const fullSteps = (howTo ?? []).map((s) => s.trim()).filter(Boolean);
    if (fullSteps.length <= maxSteps) {
        return { steps: fullSteps, fullSteps, truncated: false };
    }

    const headCount = 2;
    const tailCount = 1;
    const middleSlots = maxSteps - headCount - tailCount;
    const head = fullSteps.slice(0, headCount);
    const tail = fullSteps.slice(fullSteps.length - tailCount);
    const middle = fullSteps.slice(headCount, fullSteps.length - tailCount);
    const sampled = sampleEvenly(middle, middleSlots);

    return {
        steps: [...head, ...sampled, ...tail],
        fullSteps,
        truncated: true,
    };
}
